import { useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { signInUrl } from '../auth/signIn.js';
import { useDemo } from '../demo/DemoContext.jsx';
import { initials } from '../demo/store.js';
import { useUnread } from '../lib/useUnread.js';
import DemoBar from './DemoBar.jsx';
import ihsLogo from '../assets/ihs-logo-white.svg';

// Nav is declared once and rendered twice — top bar on desktop, bottom strip on
// phones — so the two can never drift apart.
const NAV = [
  { to: '/', label: 'Home', icon: 'home', end: true, show: () => true },
  { to: '/mine', label: 'My Tickets', icon: 'inbox', show: () => true },
  { to: '/new', label: 'Request Something', shortLabel: 'Request', icon: 'add_circle', show: () => true },
  { to: '/queue', label: 'Queue', icon: 'list_alt', show: (d) => d.isIT },
  { to: '/admin', label: 'Admin', icon: 'tune', show: (d) => d.isAdmin },
];

function Icon({ name }) {
  return (
    <span className="material-symbols-outlined" aria-hidden="true">
      {name}
    </span>
  );
}

export default function AppShell() {
  const { user, status } = useAuth();
  const demo = useDemo();
  const navigate = useNavigate();
  const items = NAV.filter((i) => i.show(demo));
  /* The bell (S10 part B): on the live app, how many tickets have a reply this person
   * has not read. IT staff go to the Queue, everyone else to My Tickets. The
   * walkthrough keeps the mockup's bell. */
  const unread = useUnread();
  const unreadCount = demo.walkthrough ? 0 : unread?.size || 0;
  const bellLabel = unreadCount
    ? `Notifications: ${unreadCount} ${unreadCount === 1 ? 'ticket has' : 'tickets have'} a new reply`
    : 'Notifications';
  const bellTo = demo.walkthrough || !demo.isIT ? '/mine' : '/queue';

  // Signed out: every /api call would be refused 401, and a ticket link from an
  // email would read "Could not load". Send them to sign in and back to this page.
  // Only 'anonymous' - 'error' (no /.auth/me, e.g. local dev) must not redirect.
  const signedOut = status === 'anonymous';
  useEffect(() => {
    if (signedOut) window.location.replace(signInUrl(window.location));
  }, [signedOut]);

  if (signedOut) return <p className="muted" style={{ padding: 34 }}>Taking you to sign in…</p>;

  return (
    <div className="shell">
      {demo.walkthrough ? <DemoBar /> : null}

      <header className="bar">
        <NavLink to="/" className="bar__brand">
          {/* The real IHS logo (house mark + wordmark), the same file the HR portal uses
              (ihs-hr-portal/src/brand/ihs/logo-white.svg). Reused, never redrawn. */}
          <img className="bar__logo" src={ihsLogo} alt="Intermountain Home Services" />
        </NavLink>
        <span className="bar__rule" />
        <span className="bar__label">IT Help Desk</span>

        <nav className="bar__nav" aria-label="Main">
          {items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className="navitem">
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="bar__right">
          {/* Search and the notification bell are in the mockup's chrome and are
              drawn here so the bar reads right. Neither is wired up — search
              needs tickets in a database, and notifications are Phase 5. They
              say so when clicked rather than doing nothing. The live app has no search,
              so it shows none (S10 part A); its bell counts real unread replies (part B). */}
          {demo.walkthrough ? (
            <button
              type="button"
              className="bar__search"
              onClick={() => window.alert('Search arrives with the real database — it is not part of this prototype.')}
            >
              <Icon name="search" />
              Search help…
            </button>
          ) : null}

          <button
            type="button"
            className="bar__bell"
            aria-label={bellLabel}
            onClick={() => navigate(bellTo)}
          >
            <Icon name="notifications" />
            {demo.walkthrough ? <i>2</i> : unreadCount ? <i>{unreadCount > 9 ? '9+' : unreadCount}</i> : null}
          </button>

          {status === 'authenticated' && user ? (
            <>
              <span className="bar__avatar" title={user.email}>
                {initials(user.name)}
              </span>
              <a href="/logout" className="navitem">
                Sign out
              </a>
            </>
          ) : status === 'anonymous' ? (
            <a href="/login" className="navitem">
              Sign in
            </a>
          ) : null}
        </div>
      </header>

      <main className="page">
        <Outlet />
      </main>

      <nav className="bottomnav" aria-label="Main">
        {items.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className="bottomnav__link">
            <Icon name={item.icon} />
            {item.shortLabel || item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
